/* ============================================================================
   L'EXUDE cart drawer behaviour — assets/lx-cart.js
   Loaded globally from layout/theme.liquid (cart_type == 'drawer' guard),
   alongside cart-drawer.js and lx-carousel.js.

   Responsibilities (everything Dawn already does — open/close, qty stepper,
   remove, section re-render, count bubble — is left to cart-drawer.js / cart.js
   / global.js QuantityInput; this file only adds the two new modules):

   1. <lx-cart-recs>  fetches sections/lx-cart-recs.liquid (rendered
      recommendation cards) and drops the returned <lx-carousel> in. The carousel
      upgrades on insertion (lx-carousel.js). Hides its block when empty.
   2. "+ Add" on a recommendation card  adds the product's first available
      variant via /cart/add.js (requesting the cart-drawer + cart-icon-bubble
      sections) and hands the response to the live <cart-drawer>.renderContents()
      so the drawer + header count bubble refresh through Dawn's own path.

   The reviews carousel needs no JS here: it is server-rendered <lx-carousel>
   markup that lx-carousel.js drives (auto-advance, pause-on-hover, reduced
   motion) on its own.
   ========================================================================== */
(function () {
  'use strict';

  function addConfig(body) {
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    };
  }

  /* ---- <lx-cart-recs> ----------------------------------------------------- */
  if (!customElements.get('lx-cart-recs')) {
    customElements.define(
      'lx-cart-recs',
      class LxCartRecs extends HTMLElement {
        connectedCallback() {
          if (this._loaded) return;
          this._loaded = true;

          var url = this.dataset.url;
          if (!url) {
            this.hideBlock();
            return;
          }

          var self = this;
          fetch(url)
            .then(function (r) {
              return r.text();
            })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var carousel = doc.querySelector('lx-carousel');
              var cardCount = carousel ? carousel.querySelectorAll('.lx-rec-card').length : 0;
              if (!carousel || cardCount === 0) {
                self.hideBlock();
                return;
              }
              // Assigning innerHTML upgrades the <lx-carousel> and fires its
              // connectedCallback with the track already populated.
              self.innerHTML = carousel.outerHTML;
            })
            .catch(function (e) {
              console.error('[lx-cart] recommendations failed', e);
              self.hideBlock();
            });
        }

        hideBlock() {
          var wrap = this.closest('.lx-cart__recs');
          if (wrap) wrap.remove();
          else this.remove();
        }
      }
    );
  }

  /* ---- <lx-fade> — review rotator (grid-stacked slides, opacity swap) ------ */
  /* Replaces the translateX carousel for reviews: slides all sit in the same
     grid cell, so a review is ALWAYS full width — no half-slid seams, no
     neighbour text peeking in, no width jumps. */
  if (!customElements.get('lx-fade')) {
    customElements.define(
      'lx-fade',
      class LxFade extends HTMLElement {
        connectedCallback() {
          this.slides = Array.from(this.querySelectorAll('.lxcr__slide'));
          if (this.slides.length === 0) return;
          this.i = 0;
          this.interval = parseInt(this.dataset.interval || '4200', 10);
          this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
          var host = this.querySelector('[data-lx-dots]');
          if (host && this.slides.length > 1) {
            var self = this;
            this.dots = this.slides.map(function (_, k) {
              var d = document.createElement('button');
              d.type = 'button';
              d.className = 'lx-carousel-dot';
              d.setAttribute('aria-label', 'Review ' + (k + 1));
              d.addEventListener('click', function () {
                self.go(k);
                self.restart();
              });
              host.appendChild(d);
              return d;
            });
          }
          this.go(0);
          if (this.slides.length > 1) {
            this._stop = this.stopTimer.bind(this);
            this._go = this.restart.bind(this);
            this.addEventListener('mouseenter', this._stop);
            this.addEventListener('mouseleave', this._go);
            this.restart();
            // Reset to the first review each time the drawer OPENS, so it never
            // opens mid-rotation (Fable: desktop opened on review 3). Watch the
            // cart-drawer's `active` class — deterministic, unlike an
            // IntersectionObserver against the panel's off-screen transform.
            var drawer = this.closest('cart-drawer, .lx-cart');
            if (drawer && 'MutationObserver' in window) {
              this._mo = new MutationObserver(() => {
                if (drawer.classList.contains('active')) {
                  this.go(0);
                  this.restart();
                }
              });
              this._mo.observe(drawer, { attributes: true, attributeFilter: ['class'] });
            }
          }
        }
        disconnectedCallback() {
          this.stopTimer();
          if (this._mo) this._mo.disconnect();
        }
        go(k) {
          this.i = k;
          this.slides.forEach(function (s, x) {
            s.classList.toggle('is-current', x === k);
          });
          if (this.dots)
            this.dots.forEach(function (d, x) {
              d.classList.toggle('is-active', x === k);
            });
        }
        restart() {
          this.stopTimer();
          if (this.reduced.matches) return;
          var self = this;
          this.timer = setInterval(function () {
            self.go((self.i + 1) % self.slides.length);
          }, this.interval);
        }
        stopTimer() {
          clearInterval(this.timer);
          this.timer = null;
        }
      }
    );
  }

  /* ---- "+" quick-add on a recommendation card ------------------------------ */
  function addRecommendation(button, variantId) {
    if (!variantId || button.dataset.lxBusy === '1') return;

    var cart = document.querySelector('cart-drawer');
    var sections =
      cart && typeof cart.getSectionsToRender === 'function'
        ? cart.getSectionsToRender().map(function (s) {
            return s.id;
          })
        : ['cart-drawer', 'cart-icon-bubble'];

    button.dataset.lxBusy = '1';
    button.setAttribute('disabled', 'disabled');

    fetch(window.routes.cart_add_url, addConfig({
      id: variantId,
      quantity: 1,
      sections: sections,
      sections_url: window.location.pathname,
    }))
      .then(function (r) {
        return r.json();
      })
      .then(function (response) {
        if (response && response.status) {
          // Add failed (e.g. sold out) — restore the button.
          button.dataset.lxBusy = '0';
          button.removeAttribute('disabled');
          return;
        }
        if (cart && typeof cart.renderContents === 'function') {
          // renderContents swaps #CartDrawer (full drawer) + cart-icon-bubble
          // through Dawn's own path, so the drawer and header count bubble both
          // refresh. No extra cartUpdate publish is needed (it would only cause
          // cart-drawer-items to re-fetch and re-render a second time).
          cart.renderContents(response);
        }
        // The drawer re-render replaces this button's DOM, so no manual reset.
      })
      .catch(function (e) {
        console.error('[lx-cart] add-to-cart failed', e);
        button.dataset.lxBusy = '0';
        button.removeAttribute('disabled');
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-lx-rec-plus]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    // Size match: add the variant with the SAME EU size as the item already
    // in the cart (owner). No match / sold out in that size → product page,
    // never guess a different shoe size.
    var sizes;
    try {
      sizes = JSON.parse(button.dataset.sizes || '{}');
    } catch (e) {
      sizes = {};
    }
    var wrap = button.closest('.lx-cart__recs');
    var cartSize = wrap ? wrap.dataset.lxCartSize || '' : '';
    var match = sizes[cartSize];
    if (match && match.a === 1) {
      addRecommendation(button, String(match.id));
    } else if (button.dataset.url) {
      window.location.href = button.dataset.url;
    }
  });
})();
